// 跨場域同一時刻檢視（SPEC PLAY-09，P2）：選一個時間點，列出範圍內每台攝影機那一刻的畫面（雲端片段的縮圖，
// façade GET …/vms/devices/{d}/thumbnail?at=，lib/thumbs 快取）；點一格到回放頁、從那一刻播。
//   - 只列有 recording.read 的攝影機（同回放頁）；依家戶分組；名稱篩選；一次最多 MAX_TILES 格（其餘提示縮小範圍）
//   - 時間用瀏覽器時區選；每格另標那個家戶的當地時間（跨時區場域一眼看得出是同一刻）
//   - 沒有雲端片段 / 縮圖沒開：格子寫原因，不是黑畫面；本地（NVR / SD）錄影沒有縮圖，點進回放頁照常播
//   - 本地有沒有錄影用回放時間軸同一份覆蓋（pb.timeline，lib/localCoverage）判斷：查不到不寫「沒有錄影」（第九輪 R9-P0-2、B9-03）
// data-testid：moment-page、moment-at、moment-filter、moment-tile（data-device、data-state = loading | ok | none）、moment-more
import { DateTimeInput } from '../../components/DateTimeInput';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { playbackHref } from '../../lib/links';
import { PlaybackModes } from './PlaybackPage';
import { useCloudCams } from './cloudCams';
import { useDevices } from '../../lib/useDevices';
import { camerasOf, can, fullName, type VmsDevice } from '../../lib/devices';
import { loadLocalThumb, loadThumb } from '../../lib/thumbs';
import { localCoverageAt, type LocalCoverage } from '../../lib/localCoverage';
import { cloudClipAt } from '../../lib/facade';
import { sourcePlan } from './view';
import { useTzResolver } from '../../lib/useTz';
import { browserTz, formatDateTime } from '../../lib/tz';

export const MAX_TILES = 36;

/** datetime-local 的值（瀏覽器時區、到分鐘） ↔ epoch 毫秒。 */
export function toLocalInput(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
export function fromLocalInput(v: string): number | null {
  const ms = new Date(v).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * 一格的狀態：src = 縮圖從哪裡來（cloud / local）；local = 本地覆蓋（跟回放時間軸同一份；undefined = 沒有本地來源可查）。
 */
type TileState = { at: number; url: string | null; src: 'cloud' | 'local' | null; cloud: 'ok' | 'suspect' | 'none' | 'unknown'; localKind?: 'nvr' | 'sd' | 'phone'; local?: LocalCoverage };

/** 沒有縮圖時格子寫什麼（第九輪 B9-03：只有每種來源都確定沒有才寫「這個時間沒有錄影」）。 */
export function tileNote(st: Pick<TileState, 'cloud' | 'local'>): 'no_rec_at' | 'local_has' | 'local_unknown' | 'no_cloud_at' | 'no_frame' {
  if (st.local === 'yes') return 'local_has';
  if (st.cloud === 'none') {
    if (st.local === 'no') return 'no_rec_at';
    if (st.local === 'unknown') return 'local_unknown';
    return 'no_cloud_at';
  }
  return 'no_frame';
}

function MomentTile({ cam, tree, atMs, tz, onState }: { cam: VmsDevice; tree: VmsDevice[]; atMs: number; tz: string; onState?: (key: string, at: number, ok: boolean) => void }) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<TileState | null>(null);
  const parent = cam.parentAid ? tree.find((x) => x.aid === cam.parentAid && x.homeId === cam.homeId) : undefined;
  const plan = sourcePlan(cam, parent);
  const localOnline = (parent ?? cam).presence === 'online';
  useEffect(() => {
    let alive = true;
    const atSec = Math.floor(atMs / 1000);
    // 縮圖與「這一刻有沒有雲端片段」同一個判斷（第七輪 B7-05：沒有雲端片段卻顯示一張花掉的縮圖，點下去跳到 80 分鐘後）：
    // 有雲端片段才用雲端縮圖；沒有就問有錄影的本地來源（NVR / Kinio Station 硬碟、SD 卡）那一刻的畫面
    void (async () => {
      const cloud = await cloudClipAt(cam.apiEndpoint, cam.homeId, cam.aid, atSec);
      let url: string | null = null;
      let src: TileState['src'] = null;
      if (cloud !== 'none') {
        url = await loadThumb(cam.apiEndpoint, cam.homeId, cam.aid, atMs);
        if (url) src = 'cloud';
      }
      let local: LocalCoverage | undefined;
      // 有本地錄影可查才問：NVR / Kinio Station 的通道；Wi-Fi 攝影機在線時一律問（跟單路回放同一個判斷，回放頁一律問 pb.timeline；
      // 第十輪 B10-05：PAC142-T41 格子只寫「沒有雲端錄影」，回放頁卻說 SD 卡讀不到——façade 的 storage_kinds 不一定列出記憶卡）。
      // 設備說不支援（沒有 SD）→ absent，照寫「沒有雲端錄影」；離線時只有確定有記憶卡的才寫「讀不到 SD 卡」。
      const sdKnown = !!cam.storageKinds?.includes('sd');
      const hasLocal = plan.localKind === 'nvr' || (plan.localKind === 'sd' && (sdKnown || localOnline));
      if (!url && cloud !== 'ok' && hasLocal) {
        // 本地有沒有錄影看回放時間軸同一份覆蓋（pb.timeline），不看縮圖成功與否；離線 = 不知道
        local = localOnline ? await localCoverageAt(cam.apiEndpoint, cam.homeId, plan.localAid, plan.localCh, atSec, tz) : 'unknown';
        if (local === 'absent') local = undefined;
        if (local === 'yes' || local === 'unknown') {
          url = localOnline ? await loadLocalThumb(cam.apiEndpoint, cam.homeId, plan.localAid, plan.localCh, atMs) : null;
          if (url) {
            src = 'local';
            local = 'yes';
          }
        }
      }
      if (!alive) return;
      setState({ at: atMs, url, src, cloud, localKind: plan.localKind, local });
      // 「整頁都沒有畫面」只在每格都確定沒有時才說（本地有錄影但沒縮圖、本地讀不到都不算沒有）
      onState?.(cam.key, atMs, !!url || local === 'yes' || local === 'unknown');
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cam.apiEndpoint, cam.homeId, cam.aid, atMs, localOnline, tz]);
  const cur = state && state.at === atMs ? state : null;
  const st = !cur ? 'loading' : cur.url ? 'ok' : cur.local === 'yes' ? 'local' : cur.local === 'unknown' ? 'unknown' : 'none';
  const suspect = cur?.cloud === 'suspect' && cur.src === 'cloud';
  // 點格子：縮圖是雲端的才指定雲端；本地的指定本地；都沒有就不指定，回放頁自己挑那一刻有錄影的來源（不強制雲端跳到別的時間）
  const src = cur?.src === 'cloud' && !suspect ? { src: 'cloud' as const } : cur?.src === 'local' || cur?.local === 'yes' ? { src: 'local' as const } : {};
  const localName = t(`playback.src_${cur?.localKind === 'nvr' ? 'nvr' : 'sd'}`);
  return (
    <Link
      className="moment-tile card"
      // 點格子 = 直接開這台的回放、帶這個時間（P2-5；回放頁從 t − 5 秒開始播，所以 + 5 落在這一刻）
      to={playbackHref(cam.key, Math.floor(atMs / 1000) + 5, src)}
      data-testid="moment-tile"
      data-device={cam.aid}
      data-state={st}
      data-src={cur?.src ?? undefined}
    >
      <div className="moment-img">
        {cur?.url ? (
          <img src={cur.url} alt="" />
        ) : (
          <span className="muted small" data-testid="moment-no-frame">
            {/* 只有每種來源都確定沒有才寫「這個時間沒有錄影」；本地讀不到寫讀不到（第九輪 B9-03，取代第八輪 B8-08 的寫法） */}
            {st === 'loading' || !cur ? t('common.loading') : t('moment.' + tileNote(cur), { src: localName })}
          </span>
        )}
      </div>
      {cur?.src === 'local' && (
        <div className="small muted" data-testid="moment-local-src">
          {t('moment.from_local', { src: t(`playback.src_${cur.localKind === 'nvr' ? 'nvr' : 'sd'}`) })}
        </div>
      )}
      {suspect && (
        <div className="small moment-suspect" title={t('moment.clip_suspect_hint')} data-testid="moment-clip-suspect">
          {t('moment.clip_suspect')}
        </div>
      )}
      <div className="small">{fullName(cam, tree)}</div>
      <div className="muted small">{formatDateTime(Math.floor(atMs / 1000), tz, i18n.language, true)}</div>
    </Link>
  );
}

export function MomentPage() {
  const { t } = useTranslation();
  const { tree, loading } = useDevices();
  const tzOf = useTzResolver();
  const cameras = useMemo(() => camerasOf(tree).filter((c) => can(c, 'recording.read')), [tree]);
  // 從別的分段來：?t=<unix 秒>&cam=<第一台>（第二輪 B2-10）
  const [sp] = useSearchParams();
  const tParam = Number(sp.get('t'));
  const camParam = sp.get('cam')?.split(',')[0] ?? '';
  const [atMs, setAtMs] = useState(() => (tParam > 0 ? Math.floor((tParam * 1000) / 60_000) * 60_000 : Math.floor((Date.now() - 3600_000) / 60_000) * 60_000));
  const [q, setQ] = useState('');
  const cloud = useCloudCams(cameras);
  const shown = cameras.filter((c) => !q || fullName(c, tree).toLowerCase().includes(q.toLowerCase()));
  const page = shown.slice(0, MAX_TILES);
  // 每格這一刻有沒有雲端畫面（全部都沒有時整頁說一次原因與下一步，第二輪 R2-P0-3 ⑤）
  const [frames, setFrames] = useState<Record<string, { at: number; ok: boolean }>>({});
  const onState = useCallback((key: string, at: number, ok: boolean) => setFrames((m) => (m[key]?.at === at && m[key]?.ok === ok ? m : { ...m, [key]: { at, ok } })), []);
  const allEmpty = page.length > 0 && page.every((c) => frames[c.key]?.at === atMs && !frames[c.key].ok);
  const groups = new Map<string, VmsDevice[]>();
  for (const c of page) groups.set(c.homeId, [...(groups.get(c.homeId) ?? []), c]);
  return (
    <div className="page" data-testid="moment-page">
      <div className="toolbar">
        <h1 className="page-title" style={{ margin: 0 }}>
          {t('moment.title')}
        </h1>
        <span className="spacer" />
        <PlaybackModes mode="moment" cameras={camParam ? [camParam] : page[0] ? [page[0].key] : []} at={Math.floor(atMs / 1000)} />
      </div>
      <p className="muted">{t('moment.help')}</p>
      <div className="toolbar wrap">
        <label>
          {t('moment.at')}{' '}
          <DateTimeInput
            value={toLocalInput(atMs)}
            max={toLocalInput(Date.now())}
            onChange={(v) => {
              const ms = fromLocalInput(v);
              if (ms !== null && ms <= Date.now()) setAtMs(ms);
            }}
            testId="moment-at"
          />
        </label>
        <span className="muted small">{t('moment.browser_tz', { tz: browserTz() })}</span>
        <input className="input" placeholder={t('moment.filter')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('moment.filter')} data-testid="moment-filter" />
      </div>
      {cloud.summary === 'none' && (
        <p className="notice small" data-testid="moment-no-cloud">
          {t('moment.no_cloud_scope')}{' '}
          <Link to="/billing#seats">{t('playback.buy_seats')}</Link>
        </p>
      )}
      {allEmpty && (
        <p className="notice small" data-testid="moment-all-empty">
          {t('moment.all_empty')}{' '}
          <Link className="btn btn-sm" to={playbackHref(page[0].key, Math.floor(atMs / 1000) + 5)} data-testid="moment-go-single">
            {t('moment.go_single')}
          </Link>
        </p>
      )}
      {loading && <p className="muted">{t('common.loading')}</p>}
      {!loading && cameras.length === 0 && <p className="muted">{t('moment.none')}</p>}
      {[...groups.entries()].map(([homeId, cams]) => (
        <section key={homeId} className="stack">
          <h2 className="section-title">{cams[0].homeName}</h2>
          <div className="moment-grid">
            {cams.map((c) => (
              <MomentTile key={c.key} cam={c} tree={tree} atMs={atMs} tz={tzOf(c.homeId)} onState={onState} />
            ))}
          </div>
        </section>
      ))}
      {shown.length > MAX_TILES && (
        <p className="muted" data-testid="moment-more">
          {t('moment.more', { n: shown.length - MAX_TILES, max: MAX_TILES })}
        </p>
      )}
    </div>
  );
}
